import { Guest } from '@/types';

/**
 * CSV helpers for importing / exporting the guest list.
 *
 * Spanish column headers are used because this surface is user-facing
 * (planners open the template in Excel / Google Sheets).
 */

export const GUEST_CSV_HEADERS = ['Nombre', 'Teléfono', 'Estado'] as const;

export const GUEST_CSV_TEMPLATE_FILENAME = 'plantilla-invitados.csv';

/**
 * Hard cap on how many rows a single CSV import may contain. Anything larger
 * is blocked at the UI layer to avoid timing out the bulk-insert request and
 * to keep the optimistic UI update bounded.
 */
export const MAX_GUEST_IMPORT_ROWS = 500;

const VALID_ESTADOS = ['PENDIENTE', 'CONFIRMADO', 'RECHAZADO'] as const;
export type GuestEstado = (typeof VALID_ESTADOS)[number];

export interface ParsedGuestRow {
  /** 1-indexed row number in the original CSV (1 = header). */
  rowNumber: number;
  nombre: string;
  tel: string;
  estado: GuestEstado;
}

export interface ParsedGuestError {
  rowNumber: number;
  message: string;
}

export interface ParseGuestCsvResult {
  guests: ParsedGuestRow[];
  errors: ParsedGuestError[];
}

/**
 * Escape a single CSV field. Wraps in double quotes if the value contains
 * a comma, quote, or newline; doubles any inner double quotes per RFC 4180.
 */
const escapeField = (value: string | undefined | null): string => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/**
 * Parse a single CSV line into fields. Handles quoted fields with embedded
 * commas and escaped double quotes. Returns null if the line is empty/whitespace.
 */
const parseCsvLine = (line: string): string[] | null => {
  if (line.trim().length === 0) return null;

  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      current += ch;
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ',') {
      fields.push(current);
      current = '';
      i++;
      continue;
    }
    current += ch;
    i++;
  }

  fields.push(current);
  return fields;
};

/**
 * Split a CSV blob into logical lines. Strips a leading UTF-8 BOM if present
 * and normalizes CRLF / CR to LF.
 */
const splitCsvLines = (csv: string): string[] => {
  // Strip leading UTF-8 BOM (\uFEFF) if present.
  const stripped = csv.replace(/^\uFEFF/, '');
  return stripped.replace(/\r\n?/g, '\n').split('\n');
};

const normalizeHeader = (h: string): string =>
  h
    .trim()
    .toLowerCase()
    // strip Spanish accents so "Teléfono" matches "telefono"
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const HEADER_ALIASES: Record<string, 'nombre' | 'tel' | 'estado'> = {
  nombre: 'nombre',
  name: 'nombre',
  telefono: 'tel',
  tel: 'tel',
  telephone: 'tel',
  phone: 'tel',
  whatsapp: 'tel',
  estado: 'estado',
  status: 'estado',
  rsvp: 'estado',
};

const normalizeEstado = (raw: string): GuestEstado | null => {
  if (!raw) return 'PENDIENTE';
  const v = normalizeHeader(raw);
  if (['pendiente', 'pending', ''].includes(v)) return 'PENDIENTE';
  if (['confirmado', 'confirmada', 'confirmed', 'si', 'sí', 'yes'].includes(v))
    return 'CONFIRMADO';
  if (['rechazado', 'rechazada', 'declined', 'no'].includes(v)) return 'RECHAZADO';
  return null;
};

/**
 * Build the CSV template string. With `guests` empty, returns a header row
 * plus a single illustrative sample row so the user can see the format.
 * With `guests` populated, returns the headers + one row per guest (used by
 * the Exportar action).
 */
export const buildGuestCsv = (guests: Guest[] = []): string => {
  const lines: string[] = [GUEST_CSV_HEADERS.join(',')];

  if (guests.length === 0) {
    lines.push(
      [escapeField('María García'), escapeField('+52 55 1234 5678'), 'PENDIENTE'].join(','),
    );
  } else {
    for (const g of guests) {
      lines.push(
        [escapeField(g.nombre), escapeField(g.tel), escapeField(g.estado)].join(','),
      );
    }
  }

  // Trailing newline so files end cleanly when re-saved by Excel.
  return lines.join('\n') + '\n';
};

/**
 * Parse a CSV blob into guest rows + per-row errors. Header row is required.
 * Unknown columns are ignored; "Estado" defaults to PENDIENTE when missing.
 */
export const parseGuestCsv = (csv: string): ParseGuestCsvResult => {
  const guests: ParsedGuestRow[] = [];
  const errors: ParsedGuestError[] = [];

  const lines = splitCsvLines(csv);
  if (lines.length === 0) {
    return { guests, errors: [{ rowNumber: 0, message: 'El archivo está vacío.' }] };
  }

  // Find first non-empty line as header
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().length > 0) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) {
    return { guests, errors: [{ rowNumber: 0, message: 'El archivo está vacío.' }] };
  }

  const headerFields = parseCsvLine(lines[headerIdx]) ?? [];
  const headerMap: Record<number, 'nombre' | 'tel' | 'estado'> = {};
  headerFields.forEach((h, idx) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key) headerMap[idx] = key;
  });

  if (!Object.values(headerMap).includes('nombre') || !Object.values(headerMap).includes('tel')) {
    return {
      guests,
      errors: [
        {
          rowNumber: headerIdx + 1,
          message:
            'Encabezados inválidos. Se requieren al menos las columnas "Nombre" y "Teléfono".',
        },
      ],
    };
  }

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const rowNumber = i + 1;
    const fields = parseCsvLine(lines[i]);
    if (!fields) continue;

    const record: { nombre?: string; tel?: string; estado?: string } = {};
    fields.forEach((value, idx) => {
      const key = headerMap[idx];
      if (key) record[key] = value;
    });

    const nombre = (record.nombre ?? '').trim();
    const tel = (record.tel ?? '').trim();
    const estadoRaw = (record.estado ?? '').trim();

    if (!nombre && !tel && !estadoRaw) continue; // silently skip blank rows
    if (!nombre) {
      errors.push({ rowNumber, message: 'Falta el nombre.' });
      continue;
    }
    if (!tel) {
      errors.push({ rowNumber, message: 'Falta el teléfono.' });
      continue;
    }

    const estado = normalizeEstado(estadoRaw);
    if (estado === null) {
      errors.push({
        rowNumber,
        message: `Estado inválido: "${estadoRaw}". Use PENDIENTE, CONFIRMADO o RECHAZADO.`,
      });
      continue;
    }

    guests.push({ rowNumber, nombre, tel, estado });
  }

  return { guests, errors };
};

/**
 * Trigger a browser download for the given CSV string. Prepends a UTF-8 BOM
 * so Excel correctly renders accented characters (María, Teléfono, etc.).
 */
export const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
